/*
 * Inbox service
 * Purpose: Centralize AI Inbox API requests and row normalization for the inbox page.
 * Implementation notes: Uses existing dataService route keys and preserves nullable provenance fields without inventing client-only data.
 * Recent changes: ActionTitle is now the preferred display value; category-prefix parsing remains as legacy fallback.
 */

import { API_ENDPOINTS } from '../constants/apiEndpoints';
import {
  INBOX_ACTION_CATEGORIES,
  INBOX_ACTION_CATEGORY_LABELS,
  INBOX_STATUSES,
  InboxActionCategory,
  InboxItem,
  InboxStatus
} from '../models/InboxItem';
import { request } from './dataService';

type InboxApiRecord = Partial<InboxItem> & {
  inboxId: number;
  teamId: number;
  actionText: string;
  status?: string | null;
};

interface InboxFilters {
  teamId?: number | null;
  status?: InboxStatus | null;
  accountId?: number | null;
  contactId?: number | null;
  actionKey?: string | null;
  sourcePath?: string | null;
  q?: string | null;
  signals?: string | null;
}

const isInboxStatus = (value: string | null | undefined): value is InboxStatus =>
  Boolean(value && INBOX_STATUSES.includes(value as InboxStatus));

const isInboxActionCategory = (value: string | null | undefined): value is InboxActionCategory =>
  Boolean(value && INBOX_ACTION_CATEGORIES.includes(value as InboxActionCategory));

const normalizeNullableText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
};

const normalizeNullableDate = (value: unknown): string | null => {
  if (!value) {
    return null;
  }

  return String(value);
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const categoryPattern = new RegExp(
  `^\\s*\`?(${INBOX_ACTION_CATEGORIES
    .slice()
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|')})\`?\\s*(?::|-|\\u2013|\\u2014)\\s*`,
  'i'
);

export const parseInboxActionPresentation = (actionText: string): {
  actionCategory: InboxActionCategory | null;
  actionCategoryLabel: string | null;
  displayActionText: string;
} => {
  const match = actionText.match(categoryPattern);
  if (!match) {
    return {
      actionCategory: null,
      actionCategoryLabel: null,
      displayActionText: actionText.trim()
    };
  }

  const normalizedCategory = match[1].toLowerCase() as InboxActionCategory;
  return {
    actionCategory: normalizedCategory,
    actionCategoryLabel: INBOX_ACTION_CATEGORY_LABELS[normalizedCategory],
    displayActionText: actionText.slice(match[0].length).trim() || actionText.trim()
  };
};

export const normalizeInboxItem = (item: InboxApiRecord): InboxItem => {
  const actionPresentation = parseInboxActionPresentation(item.actionText);
  const actionTitle = normalizeNullableText(item.actionTitle);
  const rawActionCategory = normalizeNullableText(item.actionCategory);
  const actionCategory = isInboxActionCategory(rawActionCategory) ? rawActionCategory : null;
  const actionCategoryLabel = actionCategory && INBOX_ACTION_CATEGORY_LABELS[actionCategory]
    ? INBOX_ACTION_CATEGORY_LABELS[actionCategory]
    : actionPresentation.actionCategoryLabel;

  return {
    inboxId: item.inboxId,
    teamId: item.teamId,
    teamName: normalizeNullableText(item.teamName),
    accountId: typeof item.accountId === 'number' ? item.accountId : null,
    accountName: normalizeNullableText(item.accountName),
    contactId: typeof item.contactId === 'number' ? item.contactId : null,
    contactName: normalizeNullableText(item.contactName),
    sourceDate: normalizeNullableDate(item.sourceDate),
    actionTitle,
    actionText: item.actionText,
    displayActionText: actionTitle || actionPresentation.displayActionText,
    actionCategory: actionCategory || actionPresentation.actionCategory,
    actionCategoryLabel,
    status: isInboxStatus(item.status) ? item.status : 'open',
    firstSeenDate: normalizeNullableDate(item.firstSeenDate),
    lastSeenDate: normalizeNullableDate(item.lastSeenDate),
    sourcePath: normalizeNullableText(item.sourcePath),
    sourceActionsReportPath: normalizeNullableText(item.sourceActionsReportPath),
    sourceSummaryPath: normalizeNullableText(item.sourceSummaryPath),
    sourceMarkdownSection: normalizeNullableText(item.sourceMarkdownSection),
    traceMarkdown: normalizeNullableText(item.traceMarkdown),
    insightText: normalizeNullableText(item.insightText),
    tensionsText: normalizeNullableText(item.tensionsText),
    memoryText: normalizeNullableText(item.memoryText),
    evidenceDate: normalizeNullableDate(item.evidenceDate),
    removedDate: normalizeNullableDate(item.removedDate),
    removalReason: normalizeNullableText(item.removalReason),
    actionKey: normalizeNullableText(item.actionKey),
    createdAt: normalizeNullableDate(item.createdAt),
    updatedAt: normalizeNullableDate(item.updatedAt)
  };
};

export const getInboxItems = async (filters: InboxFilters = {}): Promise<InboxItem[]> => {
  const rows = await request(API_ENDPOINTS.GET_INBOX, filters);
  return (Array.isArray(rows) ? rows : []).map((row) => normalizeInboxItem(row as InboxApiRecord));
};

export const updateInboxStatus = async (inboxId: number, status: InboxStatus): Promise<InboxItem> => {
  const updatedItem = await request(API_ENDPOINTS.PATCH_INBOX, { inboxId, status });
  return normalizeInboxItem(updatedItem as InboxApiRecord);
};
